import React, { useEffect, useState } from "react"

import SSOLoaderCard from "components/common/ssoLoaderCard"
import { OpenIdSSOProvider } from "hooks/openIdSSO/useOpenIdSSO"
import Cookies from "js-cookie"
import { useTranslation } from "react-i18next"
import { UserRole } from "ts/enums/User"
import { IAuthData } from "ts/interfaces/Auth"
import { getAuthCookieDomain } from "utils/domain"

interface IOpenIdResponseData {
  token: string
  finalization_required: boolean
  first_name?: string
  last_name?: string
}

interface IOpenIdSSOLoaderProps {
  provider: OpenIdSSOProvider
  isLoading: boolean
  userType: "teacher" | "student"
  onReset?: () => void
  onAuthSuccess?: (authData: IAuthData) => void
}

const loadingTextKeyByProvider: Record<OpenIdSSOProvider, string> = {
  teachhub: "noAuthFlow.teachHubSSOLoading",
  classlink: "noAuthFlow.classLinkSSOLoading",
}

const OpenIdSSOLoader: React.FC<IOpenIdSSOLoaderProps> = ({
  provider,
  isLoading,
  userType,
  onReset,
  onAuthSuccess,
}) => {
  const { t } = useTranslation()
  const [showError, setShowError] = useState(false)
  const [parsingError, setParsingError] = useState(false)

  const parseCookieData = (): IOpenIdResponseData | null => {
    const authData = Cookies.get("auth_data")

    if (!authData) {
      return null
    }

    try {
      // decoding string with escaped characters
      const decodedString = authData
        .replace(/\\"/g, "\"") // \" -> "
        .replace(/\\054/g, ",") // \054 -> , (ASCII code for comma)

      return JSON.parse(decodedString)
    } catch {
      return null
    }
  }

  const authorizeUser = (parsedData: IOpenIdResponseData) => {
    let authData: IAuthData
    try {
      authData = {
        token: parsedData.token,
        role: userType === "teacher" ? UserRole.TEACHER : UserRole.STUDENT,
        finalized: !parsedData.finalization_required,
        ...(provider === "teachhub"
          ? { isTeachHubLogin: true }
          : { isClassLinkLogin: true }),
        first_name: parsedData.first_name,
        last_name: parsedData.last_name,
      }
    } catch {
      setParsingError(true)
      setShowError(true)
      return
    }

    onAuthSuccess?.(authData)
  }

  useEffect(() => {
    if (!isLoading) {
      return
    }

    setShowError(false)
    setParsingError(false)

    // Add small delay to allow cookies to be set
    const checkDataTimer = setTimeout(() => {
      const parsedData = parseCookieData()
      if (parsedData && !!parsedData.token) {
        authorizeUser(parsedData)
      }
    }, 500)

    const errorTimer = setTimeout(() => {
      const parsedData = parseCookieData()
      if (!parsedData || !parsedData.token) {
        setShowError(true)
      }
    }, 10000)

    return () => {
      clearTimeout(checkDataTimer)
      clearTimeout(errorTimer)
    }
  }, [isLoading])

  if (!isLoading) {
    return null
  }

  const handleReset = () => {
    setShowError(false)
    setParsingError(false)
    Cookies.remove("auth_data", { domain: getAuthCookieDomain() })
    onReset?.()
  }

  return (
    <SSOLoaderCard
      showError={showError}
      loadingText={t(loadingTextKeyByProvider[provider])}
      errorText={
        parsingError
          ? t("noAuthFlow.ssoParsingError")
          : t("noAuthFlow.ssoMissingAuthData")
      }
      onReset={handleReset}
    />
  )
}

export default OpenIdSSOLoader
